import React, { useEffect, useState } from "react";
import { getProducts, addProduct, updateProduct, deleteProducts, getCategories } from "../../api/products";
import CategorySelector from "./components/CategorySelect";
import CategoryManageModal from "./components/CategoryManageModal";
import { ReactComponent as ProductAddIcon } from "../../assets/icons/ico-product-add.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/ico-plus.svg";
import { matchesChoseong } from "../../utils/search";
import { isUnlimitedStock } from "../../utils/stock";
import DeleteProductModal from "./components/DeleteProductModal";
import AddProductModal from "./components/AddProductModal";
import SearchBar from "../common/components/SearchBar";
import ImageOptionsModal from "./components/ImageOptionsModal";

// 기본 이미지
const DEFAULT_IMAGE = "/images/DEFAULT.png";

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [productNames, setProductNames] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null); // 이미지 옵션 모달용
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageOptions, setImageOptions] = useState({
    isOpen: false,
    position: { top: 0, left: 0 },
    productId: null,
    isDefaultImage: false,
  });

  const fetchProducts = async () => {
    const storeId = localStorage.getItem("selected_store_id");
    if (!storeId) {
      console.error("❌ store_id가 설정되지 않았습니다.");
      return;
    }
  
    try {
      const data = await getProducts(storeId); // ✅ store_id 추가
      setProducts(data);
    } catch (error) {
      console.error("❌ 상품 조회 실패:", error);
    }
  };

  // 카테고리는 등록 테이블(API)이 단일 출처다. 대표 이미지가 필요한 관리 모달을 위해 응답 객체를 그대로 보관한다.
  const fetchCategories = async () => {
    const data = await getCategories();
    setCategories(data);
  };

  const categoryNames = categories.map((item) => item.category);

  useEffect(() => {

    fetchProducts();
    fetchCategories();
  }, []);
  // 상품 추가
  const handleAddProduct = async (newProduct) => {
    // 실패 사유는 모달이 토스트로 보여주므로 여기서 삼키지 않고 그대로 올려보낸다
    const addedProduct = await addProduct(newProduct);
    if (addedProduct) {
      setProducts((prev) => [...prev, addedProduct]);
      setIsAddModalOpen(false);
    }
    return addedProduct;
  };

  // 상품 삭제
  const openDeleteModal = () => {
    setProductNames(products.filter((p) => selectedProducts.includes(p.id)).map((p) => p.name));
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteProducts(selectedProducts);
      setProducts(products.filter((p) => !selectedProducts.includes(p.id)));
      setSelectedProducts([]);
    } catch (error) {
      console.error("상품 삭제 실패:", error);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  // 상품 수정 (자동 저장). 여러 필드를 한 번에 보내야 하는 경우가 있다 —
  // '무한 해제'는 표식과 재고를 동시에 바꿔야 하고, 따로 저장하면 중간 상태
  // (무한 아님 + 21억)가 서버 재고 검증에 걸려 저장이 실패한다.
  const handleUpdateProductFields = async (productId, changes) => {
    const updatedProducts = products.map((item) =>
      item.id === productId ? { ...item, ...changes } : item
    );
    setProducts(updatedProducts);
    const productToUpdate = updatedProducts.find((item) => item.id === productId);
    try {
      await updateProduct(productId, productToUpdate);
    } catch (error) {
      // 낙관적으로 바꿔둔 화면을 실제 저장 실패 시 서버 값으로 되돌린다
      console.error("상품 업데이트 오류:", error);
      fetchProducts();
    }
  };

  const handleUpdateProduct = (productId, field, value) =>
    handleUpdateProductFields(productId, { [field]: value });

  // 상품 검색 (초성 검색 포함)
  const filteredProducts = products.filter((product) =>
    matchesChoseong(product.name, searchTerm));

  // 이미지 옵션 모달 열기
  const openImageModal = (product) => {
    if (!product) return;
    setSelectedImage(product);
    setIsImageModalOpen(true);
  };

  const handleImageClick = (event, productId, imageUrl) => {
    const rect = event.target.getBoundingClientRect(); // ✅ 클릭한 이미지 위치 가져오기
  
    setImageOptions({
      isOpen: true,
      position: {
        top: rect.bottom + window.scrollY - 10, // ✅ 이미지 아래에 모달 배치
        left: rect.left + window.scrollX + 20, // ✅ 이미지 중앙 정렬
      },
      productId,
      isDefaultImage: !imageUrl || imageUrl.toLowerCase().includes("default.png"),
    });
  
    console.log("📌 이미지 클릭 위치:", rect); // ✅ 디버깅용
  };

  return (
    <div className="font-body">
      {/* 상단 영역 */}
      <div className="py-2.5 flex justify-between items-center">
        <h2 className="font-600 text-2xl">상품 관리</h2>
        <div className="flex">
          {selectedProducts.length > 0 && (
            <button onClick={openDeleteModal} className="text-sm bg-red-50 text-red-500 px-8 py-2 rounded-md mr-2">
              삭제
            </button>
          )}
          <button onClick={() => setIsCategoryModalOpen(true)} className="text-sm text-gray-700 px-4 py-2 rounded-md border mr-2">
            <div className="flex gap-1">
              <PlusIcon className="w-4 pt-0.5" /> 카테고리 추가
            </div>
          </button>
          <button onClick={() => setIsAddModalOpen(true)} className="text-sm text-gray-700 px-4 py-2 rounded-md border">
            <div className="flex gap-1">
              <ProductAddIcon className="w-4 pt-0.5" /> 상품 추가
            </div>
          </button>
        </div>
      </div>

      {/* 검색창 */}
      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} placeholder="상품명으로 검색해 보세요" />

      {/* 상품 리스트 */}
      <table className="mt-4 w-full">
        <thead className="text-sm text-gray-400">
          <tr className="h-[50px]">
            <th className="px-4">
              <input type="checkbox" onChange={(e) => setSelectedProducts(e.target.checked ? products.map((p) => p.id) : [])} />
            </th>
            <th></th>
            <th className="font-400 px-4 text-start">상품명</th>
            <th className="font-400 px-4 text-start">가격</th>
            <th>카테고리</th>
            <th>수량</th>
          </tr>
        </thead>
        <tbody>
          {filteredProducts.map((product) => (
            <tr key={product.id}>
              <td className="p-4">
                <input
                  type="checkbox"
                  checked={selectedProducts.includes(product.id)}
                  onChange={() =>
                    setSelectedProducts((prev) =>
                      prev.includes(product.id) ? prev.filter((id) => id !== product.id) : [...prev, product.id]
                    )
                  }
                />
              </td>
              <td className="px-4">
                <div className="w-8 h-8 overflow-hidden rounded-md cursor-pointer" onClick={() => openImageModal(product)}>
                  <img
                    src={product.imageUrl || DEFAULT_IMAGE}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => (e.target.src = DEFAULT_IMAGE)}
                    onClick={(event) => handleImageClick(event, product.id, product.imageUrl)}
                  />
                </div>
              </td>
              <td className="px-4">
                <input type="text" value={product.name} onChange={(e) => handleUpdateProduct(product.id, "name", e.target.value)} />
              </td>
              <td className="px-4 flex items-center">
                <input
                  type="text"
                  className="text-right"
                  value={Number(product.price).toLocaleString()}
                  onChange={(e) => handleUpdateProduct(product.id, "price", e.target.value.replace(/[^0-9]/g, ""))}
                />
                <span className="ml-1">원</span>
              </td>
              <td>
                <CategorySelector
                  categories={categoryNames}
                  selectedCategory={product.category}
                  onSelect={(category) => handleUpdateProduct(product.id, "category", category)}
                />
              </td>
              <td>
                <div className="flex items-center justify-center gap-2">
                  <label className="flex items-center gap-1 cursor-pointer whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={isUnlimitedStock(product)}
                      // 무한을 풀 때 재고를 0으로 함께 내린다. 예전 값(21억 등)을 그대로 두면
                      // 수량 관리로 바꿨는데 사실상 무한인 상태가 되고, 서버 검증에도 걸린다.
                      onChange={(e) => handleUpdateProductFields(product.id,
                        e.target.checked
                          ? { unlimited_stock: true }
                          : { unlimited_stock: false, stock: 0 })}
                      className="w-4 h-4"
                    />
                    {/* 열 제목이 '수량'이므로 '제한 없음'과 합쳐 읽으면 모달의 '수량 제한 없음'과 같아진다 */}
                    <span className="text-sm text-gray-500 whitespace-nowrap">제한 없음</span>
                  </label>
                  {!isUnlimitedStock(product) && (
                    <input
                      type="number" min="0" className="w-20 text-right"
                      value={product.stock}
                      onChange={(e) => handleUpdateProduct(product.id, "stock", e.target.value)}
                    />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 모달들 — 닫을 때 언마운트해야 입력값·선택한 사진이 다음 등록에 남지 않는다 */}
      {isAddModalOpen && (
        <AddProductModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddProduct}
          categories={categoryNames}
          setProducts={setProducts}
          fetchProducts={fetchProducts}
        />
      )}
      {isCategoryModalOpen && (
        <CategoryManageModal
          isOpen={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          categories={categories}
          onChanged={fetchCategories}
        />
      )}
      <DeleteProductModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onDelete={confirmDelete} productNames={productNames} />
      {imageOptions.isOpen && (
        <ImageOptionsModal
          isOpen={imageOptions.isOpen}
          onClose={() => setImageOptions({ ...imageOptions, isOpen: false })}
          position={imageOptions.position}
          productId={imageOptions.productId}
          isDefaultImage={imageOptions.isDefaultImage}
          onUpdateImage={(newImageUrl) => {
            setProducts((prevProducts) =>
              prevProducts.map((p) =>
                p.id === imageOptions.productId ? { ...p, imageUrl: newImageUrl } : p
              )
            );
            setImageOptions({ ...imageOptions, isOpen: false }); // 모달 닫기
          }}
        />
      )}
    </div>
  );
};

export default ProductManagement;